// ToDoRequests.js
import React, { useEffect, useState } from 'react'
import { useTrans } from '../../../../services/i18n'
import SpacingContainerBase from '../../../../components/layouts/SpacingContainerBase'
import { Paper, SpacingContainer } from '../../../../components/common/Containers'
import { Divider } from '../../../../components/common/Divider'
import Typography from '../../../../components/common/Typography'
import { Grid, IconButton } from '@material-ui/core'
import { LoaderSmall } from '../../../../components/common/LoaderSmall'
import NothingFoundCard from '../../../../components/generics/Cards/NothingFoundCard'
import { Button } from '../../../../components/common/Buttons'
import { TextField } from '../../../../components/common/Inputs'
import ConfirmDialog from '../../../../components/generics/Confirm/Dialog'
import { IconDelete } from '../../../../components/svg/Icons'
import StatusChip from './statusChip'
import AddToDoRequestModal from './addToDoRequestModal'
import EditIcon from '@mui/icons-material/Edit'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import connect from '../connect'
import useBooleanHandlers from '../../../../hooks/useBooleanHandlers'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import ReplayIcon from '@material-ui/icons/Replay'
import Tooltip from '@material-ui/core/Tooltip'

// helpers
const ellipsis = (str, n = 20) => {
  if (!str) return ''
  const s = String(str)
  return s.length > n ? `${s.slice(0, n)}…` : s
}
const norm = (v) => String(v || '').trim().toLowerCase()

const ToDoRequests = ({
  detail,
  toDoRequests,
  pendingToDoRequests,
  getToDoRequests,
  updateToDoRequest,
  deleteToDoRequest,
  createToDoRequest,
  toggleToDoStatus, // <-- make sure this is mapped in connect/store
}) => {
  const { trans } = useTrans()

  const [editId, setEditId] = useState(null)
  const [form, setForm] = useState({ title: '', link: '', notes: '' })
  const [saving, setSaving] = useState(false)
  const [openRemove, onOpenRemove, onCloseRemove] = useBooleanHandlers()
  const [pendingRemove, setPendingRemove] = useState(false)
  const [toDeleteId, setToDeleteId] = useState(null)
  const [openAdd, onOpenAdd, onCloseAdd] = useBooleanHandlers()
  const [pendingToggleId, setPendingToggleId] = useState(null) // per-row toggle loader

  useEffect(() => {
    if (detail?.id) getToDoRequests({ id: detail.id })
  }, [detail?.id])

  useEffect(() => {
    if (!editId) {
      setForm({ title: '', link: '', notes: '' })
      return
    }
    const row = (toDoRequests || []).find(r => r.id === editId) || {}
    setForm({
      title: row.request || '',
      link: row.link || '',
      notes: row.notes || ''
    })
  }, [editId, toDoRequests])

  const hasItems = Array.isArray(toDoRequests) && toDoRequests.length > 0
  const onChange = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))

  const onSaveEdit = async () => {
    if (!editId) return
    setSaving(true)
    try {
      await updateToDoRequest({
        id: editId,
        title: (form.title || '').trim(),
        link: (form.link || '').trim(),
        notes: (form.notes || '').trim()
      })
      await getToDoRequests({ id: detail.id })
      setEditId(null)
    } finally {
      setSaving(false)
    }
  }

  const askDelete = (id) => {
    setToDeleteId(id)
    onOpenRemove()
  }

  const onConfirmDelete = async () => {
    if (!toDeleteId) return
    setPendingRemove(true)
    try {
      await deleteToDoRequest({ id: toDeleteId })
      await getToDoRequests({ id: detail.id })
    } finally {
      setPendingRemove(false)
      onCloseRemove()
      setToDeleteId(null)
    }
  }

  const onToggleStatus = async (id) => {
    if (!id || !detail?.id) return
    setPendingToggleId(id)
    try {
      await toggleToDoStatus({ id }) // backend flips between "Not Started" and "Completed"
      await getToDoRequests({ id: detail.id })
    } finally {
      setPendingToggleId(null)
    }
  }

  const toggleLabelFor = (status) => {
    const s = norm(status)
    if (s === 'completed' || s === 'complete' || s === 'done') {
      return trans('mark-not-started') || 'Mark Not Started'
    }
    return trans('mark-completed') || 'Mark Completed'
  }

  return (
    <SpacingContainerBase py={3}>
      <Paper component={SpacingContainer}>
        <SpacingContainer px={5} py={3}>
          <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
            <Grid item zeroMinWidth>
              <Typography variant="h4" weight="500" fontFamily="lexend">
                {trans('to-do-requests')}
              </Typography>
            </Grid>
            <Grid item>
              <Button color="primary" variant="contained" onClick={onOpenAdd}>
                {trans('add-request')}
              </Button>
            </Grid>
          </Grid>
        </SpacingContainer>

        <Divider light />

        <SpacingContainer px={5} py={2}>
          {pendingToDoRequests && <LoaderSmall text={false} />}

          {!pendingToDoRequests && !hasItems && (
            <div style={{ textAlign: 'center', marginTop: 10 }}>
              <NothingFoundCard
                title={trans('no-to-do-requests-yet')}
                size="medium"
              />
            </div>
          )}

          {!pendingToDoRequests && hasItems && (
            <TableContainer component={Paper} radius={9}>
              <Table style={{ minWidth: 860 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="left">Title</TableCell>
                    <TableCell align="left">Link</TableCell>
                    <TableCell align="left">Notes</TableCell>
                    <TableCell align="left">Status</TableCell>
                    <TableCell align="right">{trans('actions') || 'Actions'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {toDoRequests.map((t) => {
                    const isEditing = editId === t.id
                    return (
                      <TableRow key={t.id}>
                        {/* Title (request) */}
                        <TableCell align="left" style={{ width: 220 }}>
                          {isEditing ? (
                            <TextField
                              name="title"
                              value={form.title}
                              onChange={onChange}
                              fullWidth
                              placeholder={trans('title') || 'Title'}
                            />
                          ) : (
                            <Typography noWrap title={t.request || ''}>
                              {ellipsis(t.request, 20)}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Link (string only, trimmed) */}
                        <TableCell align="left" style={{ width: 240 }}>
                          {isEditing ? (
                            <TextField
                              name="link"
                              value={form.link}
                              onChange={onChange}
                              fullWidth
                              placeholder="https://… or /relative-path"
                            />
                          ) : (
                            <Typography noWrap title={t.link || ''}>
                              {t.link ? ellipsis(t.link, 20) : '—'}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Notes */}
                        <TableCell align="left" style={{ width: 300 }}>
                          {isEditing ? (
                            <TextField
                              name="notes"
                              value={form.notes}
                              onChange={onChange}
                              fullWidth
                              placeholder={trans('notes') || 'Notes'}
                            />
                          ) : (
                            <Typography noWrap title={t.notes || ''}>
                              {t.notes ? ellipsis(t.notes, 20): '—'}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Status */}
                        <TableCell align="left" style={{ width: 160 }}>
                          <StatusChip status={t.status} />
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="right" style={{ width: 260 }}>
                          {isEditing ? (
                            <>
                              <Button
                                variant="contained"
                                color="primary"
                                pending={saving}
                                onClick={onSaveEdit}
                                style={{ marginRight: 8 }}
                              >
                                {trans('save') || 'Save'}
                              </Button>
                              <Button onClick={() => setEditId(null)}>
                                {trans('cancel') || 'Cancel'}
                              </Button>
                            </>
                          ) : (
                            <>
                              {/* Toggle Status */}
                             {String(t.status || '').toLowerCase().includes('completed') ? (
                                <Tooltip title={trans('mark-not-started')}>
                                    <span>
                                    <IconButton
                                        onClick={() => onToggleStatus(t.id)}
                                        disabled={pendingToggleId === t.id}
                                        style={{ marginRight: 4 }}
                                    >
                                        <ReplayIcon />
                                    </IconButton>
                                    </span>
                                </Tooltip>
                                ) : (
                                <Tooltip title={trans('mark-completed')}>
                                    <span>
                                    <IconButton
                                        onClick={() => onToggleStatus(t.id)}
                                        disabled={pendingToggleId === t.id}
                                        style={{ marginRight: 4 }}
                                    >
                                        <CheckCircleOutlineIcon />
                                    </IconButton>
                                    </span>
                                </Tooltip>
                                )}


                              {/* Edit */}
                              <IconButton
                                onClick={() => setEditId(t.id)}
                                title={trans('edit') || 'Edit'}
                              >
                                <EditIcon />
                              </IconButton>

                              {/* Delete */}
                              <IconButton
                                onClick={() => askDelete(t.id)}
                                title={trans('delete') || 'Delete'}
                              >
                                <IconDelete />
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SpacingContainer>
      </Paper>

      {/* Confirm delete */}
      <ConfirmDialog
        open={openRemove}
        onClose={onCloseRemove}
        onConfirm={onConfirmDelete}
        pending={pendingRemove}
        title={trans('remove-request')}
        hideIcon={true}
        note={trans('this-to-do-will-be-removed')}
        confirmType={true}
        text={trans('delete-this-item')}
      />

      <AddToDoRequestModal
        open={openAdd}
        onClose={onCloseAdd}
        organizationId={detail?.id}
        createToDoRequest={createToDoRequest}
        getToDoRequests={getToDoRequests}
      />
    </SpacingContainerBase>
  )
}

export default connect(ToDoRequests)
